import * as React from "react";
import { ApplicationState } from "../../store";
import { connect } from "react-redux";
import { Auth0 as Auth0Service } from "../../services/Auth0";
import * as MeState from "../../store/Me";
import * as Auth0State from "../../store/Auth0";
import { Navigate } from "react-router-dom";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";

// This component
// 0. requests "me" data from server (for getting appBaseUrl)
// 1. watches appBaseUrl from redux, creates Auth0Service and pushes it redux
// 2. pulls auth0 session and pushes it to redux

interface Injected {
  me: MeState.MeState;
  auth0: Auth0State.Auth0State;
}

type OwnProps = {
  shouldRenderLogin: boolean;
  isForTenantUser: boolean;
  children?: React.ReactNode;
};

type Props = Injected &
  typeof MeState.actionCreators &
  typeof Auth0State.actionCreators &
  OwnProps;

const Auth0Inner: React.FC<Props> = (props) => {
  const user = UseLivlyLeaseUser();
  const { me, requestMe, setAuth0Service, setAuth0Session } = props;

  const requestMeOnMount = React.useRef(requestMe);

  React.useEffect(() => {
    requestMeOnMount.current();
  }, []);

  const getLocalSession = React.useCallback(() => {
    const accessToken = localStorage.getItem("access_token");
    const expiresInString = localStorage.getItem("expires_in");

    if (!accessToken || !expiresInString) {
      return null;
    }

    try {
      const expiresAt = Number(JSON.parse(expiresInString));
      if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt) {
        return null;
      }

      return {
        accessToken,
        idToken: "",
        expiresAt,
        isLoading: false,
      };
    } catch {
      return null;
    }
  }, []);

  const setAuth0SessionLocal = React.useCallback(
    (authResult: any) => {
      const expiresAt =
        typeof authResult?.expiresIn === "number"
          ? Date.now() + authResult.expiresIn * 1000
          : authResult?.expiresAt || 0;

      setAuth0Session({
        session: {
          accessToken: authResult?.accessToken || "",
          idToken: authResult?.idToken || "",
          expiresAt,
          isLoading: false,
        },
      });
    },
    [setAuth0Session],
  );

  const clearAuth0Session = React.useCallback(() => {
    setAuth0Session({
      session: {
        accessToken: "",
        idToken: "",
        expiresAt: 0,
        isLoading: false,
      },
    });
  }, [setAuth0Session]);

  // create/update Auth0 service and pull session whenever me/* config becomes available or changes
  const { appBaseUrl, auth0Audience, auth0ClientId, auth0Domain } = me || {};
  React.useEffect(() => {
    const updateAuth0Service = async () => {
      if (appBaseUrl && auth0Audience && auth0ClientId && auth0Domain) {
        const localSession = getLocalSession();
        if (localSession) {
          setAuth0Session({ session: localSession });
        }

        const auth0 = new Auth0Service({
          appBaseUrl,
          auth0Audience,
          auth0ClientId,
          auth0Domain,
        });
        setAuth0Service({ service: auth0 });

        try {
          console.log("checking session...");
          const authResult = await auth0.checkSession();
          console.log("checking session done: ", { authResult });
          if (authResult) {
            setAuth0SessionLocal(authResult);
          } else if (!localSession) {
            clearAuth0Session();
          } else {
            setAuth0Session({ session: localSession });
          }
        } catch (e) {
          if (localSession) {
            setAuth0Session({ session: localSession });
          } else {
            clearAuth0Session();
          }
        }
      }
    };

    updateAuth0Service();
  }, [
    appBaseUrl,
    auth0Audience,
    auth0ClientId,
    auth0Domain,
    getLocalSession,
    setAuth0Service,
    setAuth0Session,
    setAuth0SessionLocal,
    clearAuth0Session,
  ]);

  const {
    shouldRenderLogin,
    isForTenantUser,
    auth0: { session },
  } = props;

  if (session && session.isLoading) {
    return null;
  }

  const isLoggedIn = !!session && !!session.accessToken;

  const isTenantUser =
    user !== undefined &&
    user.activeTenantId !== undefined &&
    user.activeTenantId !== null;
  const shouldRenderChildren = isLoggedIn && isTenantUser === isForTenantUser;
  if (shouldRenderChildren) {
    return <>{props.children}</>;
  }

  if (!isLoggedIn && shouldRenderLogin) {
    //redirect to login page
    return <Navigate to="/login" />;
  }

  return <div>Loading...</div>;
};

export const Auth0: any = connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    me: state.me,
    auth0: state.auth0,
  }),
  {
    ...Auth0State.actionCreators,
    ...MeState.actionCreators,
  },
)(Auth0Inner);
